<script setup lang="ts">
import { ref } from 'vue';
import type { ItemRankingDescription } from '@/services/itemRankingDescription.service';
import itemRankingGroupService, { type ItemRankingGroup } from '@/services/itemRankingGroup.service';
import GroupTreeSelect from '@/components/GroupTreeSelect.vue';
import PolyButton from '@/components/poly/PolyButton.vue';
import { confirm } from '@/composables/modalHelper';

const $t = window.i18next.t;

// Modal "Editar ítem": recrea en Vue el formulario que hoy abre itemRanking.modal.jade (Angular)
// vía ctrl+click sobre un elemento jerarquizado — nombre de la descripción y grupos asociados,
// elegidos y gestionados (crear/renombrar/eliminar) con el mismo árbol grupo/subgrupo que usa el
// ntn-tree de Angular (GroupTreeSelect.vue), replicando itemRankingGroup.modal.jade.

const props = defineProps<{
  data: { description: ItemRankingDescription; allGroups: ItemRankingGroup[] };
  resolve: (result: { name: string; rankingGroups: { _id: string; name: string }[] } | null) => void;
}>();

const name = ref(props.data.description.name);
// fallback al campo singular rankingGroup: ítems legacy (creados antes de que existiera el array
// rankingGroups) solo lo tienen a él poblado, y sin este fallback el árbol arrancaría sin ningún
// grupo preseleccionado, dejando el botón Guardar deshabilitado de forma permanente
const initialGroups = props.data.description.rankingGroups?.length
  ? props.data.description.rankingGroups
  : (props.data.description.rankingGroup ? [props.data.description.rankingGroup] : []);
const selectedGroupIds = ref<string[]>(initialGroups.map(g => g._id));

// árbol de grupos editable en memoria: arranca desde el estado recibido y se actualiza a medida
// que se crean/renombran/eliminan grupos, para reflejar los cambios sin cerrar el modal
const groups = ref<ItemRankingGroup[]>([...props.data.allGroups]);

// crea un grupo raíz nuevo y lo agrega al árbol en memoria
async function createRoot(name_: string) {
  try {
    const newGroup = await itemRankingGroupService.save({ name: name_ });
    groups.value.push(newGroup);
  } catch (error) {
    console.error(error)
  }
}

// crea un sub-grupo del grupo indicado y lo agrega al árbol en memoria
async function createSubgroup(parentId: string, name_: string) {
  try {
    const newGroup = await itemRankingGroupService.save({ name: name_, parent: parentId });
    groups.value.push(newGroup);
  } catch (error) {
    console.error(error)
  }

}

// renombra un grupo/sub-grupo existente
async function renameGroup(groupId: string, newName: string) {
  try {
    const group = groups.value.find(g => g._id === groupId);
    if (!group) return;
    const saved = await itemRankingGroupService.save({ ...group, name: newName });
    group.name = saved.name;
  } catch (error) {
    console.error(error)
  }
}

// pide confirmación y elimina un grupo/sub-grupo (la API borra recursivamente sus descendientes)
async function deleteGroup(groupId: string) {
  try {
    const group = groups.value.find(g => g._id === groupId);
    if (!group) return;
    const confirmed = await confirm(
      $t('Eliminar grupo'),
      $t('¿Seguro que desea eliminar este grupo y/o sub-grupos?'),
    );
    if (!confirmed) return;
    await itemRankingGroupService.remove(groupId);
    // quita del árbol en memoria el grupo y todos sus descendientes (recursivo, igual que la API)
    const idsToRemove = [groupId, ...descendantIds(groupId)];
    groups.value = groups.value.filter(g => !idsToRemove.includes(g._id!));
    selectedGroupIds.value = selectedGroupIds.value.filter(id => !idsToRemove.includes(id));
  } catch (error) {
    console.error(error)
  }
}

// ids de todos los descendientes (recursivo) de un grupo, dentro del árbol en memoria
function descendantIds(groupId: string): string[] {
  const children = groups.value.filter(g => g.parent === groupId);
  return children.reduce((ids, child) => [...ids, child._id!, ...descendantIds(child._id!)], [] as string[]);
}

function save() {
  const trimmedName = name.value.trim();
  if (!trimmedName || !selectedGroupIds.value.length) return;
  const rankingGroups = selectedGroupIds.value.map(id => ({
    _id: id,
    name: groups.value.find(group => group._id === id)!.name,
  }));
  props.resolve({ name: trimmedName, rankingGroups });
}
</script>

<template>
  <div class="edit-item-ranking-modal">
    <div class="form-field">
      <label>{{ $t('Nombre del Item') }}:</label>
      <span class="item-span">{{ data.description.rankingItem }}</span>
    </div>
    <div class="form-field">
      <label>{{ $t('Por defecto') }}:</label>
      <span class="item-span">{{ data.description.visibleByDefault ? $t('Visible') : $t('No visible') }}</span>
    </div>
    <div class="form-field">
      <label>{{ $t('Nombre*') }}</label>
      <input v-model="name" type="text" class="text-input" data-testid="item-editar-nombre-input" />
    </div>
    <div class="form-field">
      <label>{{ $t('Grupos*') }}</label>
      <group-tree-select v-model="selectedGroupIds" :groups="groups" @create-root="createRoot"
        @create-subgroup="createSubgroup" @rename="renameGroup" @delete="deleteGroup" />
    </div>
    <div class="modal-actions">
      <poly-button button-style="Blank" button-color="Grey" data-testid="item-editar-cancelar-boton" @click="resolve(null)">
        {{ $t('Cerrar') }}
      </poly-button>
      <poly-button data-testid="item-editar-guardar-boton" :disabled="!name.trim() || !selectedGroupIds.length"
        @click="save">
        {{ $t('Guardar') }}
      </poly-button>
    </div>
  </div>
</template>

<style lang="less" scoped>
.edit-item-ranking-modal {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 2em;
}

.form-field {
  display: flex;
  flex-direction: column;
  gap: 6px;

  label {
    font-size: 0.8em;
    letter-spacing: 0.03em;
    color: #757575;
  }
}

.item-span {
  font-size: 14px;
  color: #444;
}

.text-input {
  padding: 8px 10px;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-family: inherit;
  font-size: 14px;
}

.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}

</style>
